import type { PullRequestActivity, PullRequestReactionContent } from "@cmmd-forge/contracts";
import { useState, type ReactNode } from "react";

import { MarkdownEditor } from "~/components/rich-content/MarkdownEditor";
import { Button } from "~/components/ui/button";
import { formatRelativeTimeLabel } from "~/timestampFormat";
import { PullRequestReactionPills } from "./PullRequestReactionPills";
import { PullRequestMarkdown } from "../PullRequestMarkdown";

export interface PullRequestTimelineItemProps {
  readonly presentation?: "timeline" | "card";
  readonly actor: string;
  readonly at: string;
  readonly title: string;
  readonly body?: string | null;
  readonly markdown?: boolean;
  readonly reactions?: PullRequestActivity["reactions"] | undefined;
  readonly reactionsEnabled?: boolean;
  readonly reactionsPending?: boolean;
  readonly canEdit?: boolean;
  readonly editPending?: boolean;
  readonly onSave?: (body: string) => Promise<void>;
  readonly onToggleReaction?: (content: PullRequestReactionContent, reacted: boolean) => void;
  readonly action?: ReactNode;
}

export function PullRequestTimelineItem({
  presentation = "timeline",
  actor,
  at,
  title,
  body,
  markdown = false,
  reactions,
  reactionsEnabled = false,
  reactionsPending = false,
  canEdit = false,
  editPending = false,
  onSave,
  onToggleReaction,
  action,
}: PullRequestTimelineItemProps) {
  const edit = useTimelineItemEditing(body, onSave);
  return (
    <article
      className={presentation === "timeline" ? "border-l-2 border-border pl-3 text-sm" : "text-sm"}
    >
      <TimelineItemHeader actor={actor} at={at} title={title} />
      <TimelineItemBody
        body={body}
        markdown={markdown}
        editing={edit.editing}
        editor={<TimelineItemEditor {...edit} pending={editPending} />}
      />
      <TimelineEditAction
        canEdit={canEdit}
        editing={edit.editing}
        pending={editPending}
        onEdit={() => edit.begin(body)}
      />
      {action}
      <TimelineItemReactions
        reactions={reactions}
        enabled={reactionsEnabled}
        pending={reactionsPending}
        onToggle={onToggleReaction}
      />
    </article>
  );
}

function useTimelineItemEditing(
  body: string | null | undefined,
  onSave: ((body: string) => Promise<void>) | undefined,
) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(body ?? "");
  const [editError, setEditError] = useState<string | null>(null);
  const save = async () => {
    if (onSave === undefined || draft.trim().length === 0) return;
    setEditError(null);
    try {
      await onSave(draft);
      setEditing(false);
    } catch {
      setEditError("Couldn’t save this comment. Your draft is still here—try again.");
    }
  };
  return {
    editing,
    draft,
    error: editError,
    onChange: setDraft,
    onCancel: () => setEditing(false),
    onSave: () => void save(),
    begin: (nextBody: string | null | undefined) => {
      setDraft(nextBody ?? "");
      setEditError(null);
      setEditing(true);
    },
  };
}

function TimelineItemHeader(props: {
  readonly actor: string;
  readonly at: string;
  readonly title: string;
}) {
  return (
    <p>
      <span className="font-medium">{props.actor}</span>
      {props.actor.length > 0 ? " " : ""}
      {props.title}{" "}
      <span className="text-muted-foreground">{formatRelativeTimeLabel(props.at)}</span>
    </p>
  );
}

function TimelineItemBody(props: {
  readonly body: string | null | undefined;
  readonly markdown: boolean;
  readonly editing: boolean;
  readonly editor: ReactNode;
}) {
  if (props.editing) return props.editor;
  if (!props.body) return null;
  return props.markdown ? (
    <PullRequestMarkdown text={props.body} className="mt-1" />
  ) : (
    <p className="mt-1 text-muted-foreground">{props.body}</p>
  );
}

function TimelineEditAction(props: {
  readonly canEdit: boolean;
  readonly editing: boolean;
  readonly pending: boolean;
  readonly onEdit: () => void;
}) {
  const visible = props.canEdit ? !props.editing : false;
  return visible ? (
    <Button
      type="button"
      size="sm"
      variant="ghost"
      disabled={props.pending}
      className="mt-1"
      onClick={props.onEdit}
    >
      Edit
    </Button>
  ) : null;
}

function TimelineItemReactions(props: {
  readonly reactions: PullRequestActivity["reactions"];
  readonly enabled: boolean;
  readonly pending: boolean;
  readonly onToggle: PullRequestTimelineItemProps["onToggleReaction"];
}) {
  return props.onToggle ? (
    <PullRequestReactionPills
      reactions={props.reactions}
      enabled={props.enabled}
      pending={props.pending}
      onToggle={props.onToggle}
    />
  ) : null;
}

function TimelineItemEditor(props: {
  readonly draft: string;
  readonly error: string | null;
  readonly pending: boolean;
  readonly onChange: (value: string) => void;
  readonly onCancel: () => void;
  readonly onSave: () => void;
}) {
  const saveDisabled = timelineItemSaveDisabled(props);
  return (
    <div className="mt-2">
      <MarkdownEditor
        value={props.draft}
        onChange={props.onChange}
        ariaLabel="Edit pull request comment"
        placeholder="Update comment…"
        density="compact"
        disabled={props.pending}
        maxLength={65_536}
      />
      <div className="mt-2 flex gap-2">
        <Button type="button" size="sm" disabled={saveDisabled} onClick={props.onSave}>
          {props.pending ? "Saving…" : "Save"}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={props.pending}
          onClick={props.onCancel}
        >
          Cancel
        </Button>
      </div>
      <TimelineItemEditError error={props.error} />
    </div>
  );
}

function timelineItemSaveDisabled(props: { readonly pending: boolean; readonly draft: string }) {
  if (props.pending) return true;
  if (props.draft.trim().length === 0) return true;
  return props.draft.length > 65_536;
}

function TimelineItemEditError({ error }: { readonly error: string | null }) {
  return error ? (
    <p role="alert" className="mt-2 text-xs text-destructive">
      {error}
    </p>
  ) : null;
}
